import produce from 'immer';
import { snakeCase } from 'lodash';
import { Controller, useFormContext } from 'react-hook-form';

import ReactSelect, { OptionType } from 'dashboard/components/ReactSelect';
import {
  EditorableSchema,
  RelationSchemaType,
} from 'dashboard/components-lib/FieldSchemaEditor/constants';
import {
  AttributeDisplay,
  AttributeGrid,
  ConfigSection,
  InputLabel,
} from 'dashboard/components-lib/FieldSchemaEditorForm/shared';
import Checkbox from 'design-system/components/Checkbox';
import Input from 'design-system/components/Input';
import Spacer from 'design-system/components/Spacer';
import { Heading, Paragraph } from 'design-system/components/Text';

interface RelationFieldControlsProps {
  fieldSchema: EditorableSchema;
  fieldName: string;
  relationObjectTypeOptions: OptionType<string>[];
  formContext: ReturnType<typeof useFormContext>;
  canEdit: (fieldSchema: EditorableSchema) => boolean;
}

const RelationFieldControls: React.FC<RelationFieldControlsProps> = ({
  fieldSchema,
  fieldName,
  relationObjectTypeOptions,
  formContext,
  canEdit,
}) => {
  const { control, setValue } = formContext;
  const findRelatedObjectLabel = (token: string) => {
    for (const relationObject of relationObjectTypeOptions ?? []) {
      for (const option of relationObject.options ?? []) {
        if (option.value === token) {
          return option.label;
        }
      }
    }
  };

  if (fieldSchema.type !== RelationSchemaType.RelationSchema) return <></>;

  if (!canEdit(fieldSchema)) {
    return (
      <>
        <Spacer height={2} />
        <InputLabel htmlFor='field-schema-panel__field-related-object-type'>
          Related object
        </InputLabel>
        <Controller
          name={`${fieldName}.token`}
          control={control}
          render={({ field: { value, onChange } }) => (
            <ReactSelect
              id='field-schema-panel__field-related-object-type'
              aria-label='Related object'
              options={relationObjectTypeOptions}
              value={value}
              onChange={onChange}
              searchEnabled
            />
          )}
        />
        <Spacer height={2} />
        <Controller
          name={`${fieldName}.cardinality`}
          control={control}
          render={({ field: { value, onChange } }) => (
            <Checkbox
              label='Is an array'
              value={value}
              checked={fieldSchema.cardinality === 'many'}
              onChange={() => onChange(fieldSchema.cardinality === 'many' ? 'one' : 'many')}
            />
          )}
        />
        {fieldSchema.token && (
          <>
            <Spacer height={2} />
            <Heading size='sm'>Add new field to related object</Heading>
            <Paragraph>
              Relation fields have bidirectional relationships. Add a new field to the related
              object above to set up its relation to this account type.
            </Paragraph>
            <Spacer height={2} />
            <InputLabel htmlFor='field-schema-panel__field-inverse-label'>Field name</InputLabel>
            <Input
              id='field-schema-panel__field-inverse-label'
              value={fieldSchema['inverse-label'] ?? ''}
              placeholder='label'
              onChange={(e) =>
                setValue(
                  fieldName,
                  produce(fieldSchema, (draftSchema) => {
                    draftSchema['inverse-label'] = e.target.value;
                    (draftSchema['inverse-key'] = snakeCase(e.target.value)),
                      (draftSchema['inverse-cardinality'] = 'one');
                  }),
                )
              }
            />
            <Spacer height={2} />
            <InputLabel htmlFor='field-schema-panel__field-inverse-key'>Key</InputLabel>
            <Input
              id='field-schema-panel__field-inverse-key'
              value={fieldSchema['inverse-key'] ?? '--'}
              placeholder='-'
              onChange={(e) => setValue(`${fieldName}.inverse-key`, e.target.value)}
            />
            <Spacer height={2} />
            <Checkbox
              label='Is an array'
              checked={fieldSchema['inverse-cardinality'] === 'many'}
              onChange={() =>
                setValue(
                  `${fieldName}.inverse-cardinality`,
                  fieldSchema['inverse-cardinality'] === 'many' ? 'one' : 'many',
                )
              }
            />
          </>
        )}
      </>
    );
  } else {
    return (
      <>
        <Spacer height={2} />
        <ConfigSection>
          <Spacer height={2} />
          <Heading size='sm'>Related object</Heading>
          <Spacer height={2} />
          <AttributeGrid colCount={2}>
            <AttributeDisplay
              title='Object'
              value={
                fieldSchema.cardinality === 'many'
                  ? `${findRelatedObjectLabel(fieldSchema.token)} (Array)`
                  : findRelatedObjectLabel(fieldSchema.token)
              }
            />
            <AttributeDisplay title='Name' value={fieldSchema['inverse-label']} />
            <AttributeDisplay title='Key' value={fieldSchema['inverse-key']} />
          </AttributeGrid>
        </ConfigSection>
      </>
    );
  }
};

export default RelationFieldControls;
